import React from 'react';
import {
  EvaluationItem,
  FixedQuestionAnswer,
  RecruiterCandidate,
  RecruiterEvaluation,
  RecruiterInterview,
} from '../../../types/recruiter';
import { CustomSkillsSection } from './evaluationView/CustomSkillsSection';
import { GenericSkillsSection } from './evaluationView/GenericSkillsSection';
import { Summary } from './evaluationView/Summary';
import { SpecializedSkillsSection } from './evaluationView/SpecializedSkillsSection';
import { Star } from 'lucide-react';

interface EvaluationDetailProps {
  selectedInterview: RecruiterInterview;
  selectedCandidate: RecruiterCandidate;
  evaluation?: RecruiterEvaluation;
}

const getSkillTotals = (skills?: {
  skill_1?: EvaluationItem;
  skill_2?: EvaluationItem;
  skill_3?: EvaluationItem;
  total?: number;
}) => {
  if (!skills) return { total: 0, max: 0 };
  const items = [skills.skill_1, skills.skill_2, skills.skill_3];
  const total = skills.total ?? items.reduce((sum, item) => sum + (item?.score ?? 0), 0);
  const max = items.reduce((sum, item) => sum + (item?.maxScore ?? 0), 0);
  return { total, max };
};

export const EvaluationDetail: React.FC<EvaluationDetailProps> = ({
  selectedInterview,
  selectedCandidate,
  evaluation,
}) => {
  const totalScore = evaluation?.totalScore ?? 0;
  const specializedSkills = evaluation?.detailedEvaluation?.specializedSkills;
  const genericSkills = evaluation?.detailedEvaluation?.genericSkills;
  const customSkills = evaluation?.detailedEvaluation?.customSkills;
  const fixedQuestionAnswers = evaluation?.detailedEvaluation?.fixedQuestionAnswers;
  const fixedQuestionsArray: FixedQuestionAnswer[] = fixedQuestionAnswers
    ? Object.values(fixedQuestionAnswers).filter((v): v is FixedQuestionAnswer => !!v && !!v.question)
    : [];
  const specializedTotals = getSkillTotals(specializedSkills);
  const genericTotals = getSkillTotals(genericSkills);
  return (
    <>
      <Summary
        selectedInterview={selectedInterview}
        selectedCandidate={selectedCandidate}
        evaluation={evaluation}
        totalScore={totalScore}
      />

      {/* 総評 */}
      {evaluation && specializedTotals && genericTotals && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">総評</h4>
          <div className="space-y-4">
            {/* 専門スキル合計 */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-lg font-medium text-gray-800">専門スキル合計</span>
                <span className="text-xl font-bold text-blue-600">
                  {specializedTotals.total}~/{specializedTotals.max}点
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                  style={{
                    width: `${specializedTotals.max > 0 ? (specializedTotals.total / specializedTotals.max) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>

            {/* 汎用スキル合計 */}
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-lg font-medium text-gray-800">汎用スキル合計</span>
                <span className="text-xl font-bold text-green-600">
                  {genericTotals.total}~/{genericTotals.max}点
                </span>
              </div>
              <div className="w-full bg-green-200 rounded-full h-3">
                <div
                  className="bg-green-600 h-3 rounded-full transition-all duration-500"
                  style={{
                    width: `${genericTotals.max > 0 ? (genericTotals.total / genericTotals.max) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>

            {/* 総合得点 */}
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-300">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xl font-semibold text-gray-900">総合得点</span>
                <span className="text-2xl font-bold text-gray-900">{totalScore}~点</span>
              </div>
              <div className="w-full bg-gray-300 rounded-full h-4">
                <div
                  className="bg-gray-700 h-4 rounded-full transition-all duration-500"
                  style={{
                    width: `${(totalScore / 100) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 評価結果 */}
      {evaluation ? (
        <div className="space-y-6">
          {/* 詳細評価 - 専門スキル */}
          {specializedSkills && (
            <SpecializedSkillsSection skills={specializedSkills} totals={specializedTotals} />
          )}

          {/* 詳細評価 - 汎用スキル */}
          {genericSkills && (
            <GenericSkillsSection skills={genericSkills} totals={genericTotals} />
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <div className="text-gray-500 mb-2">
          <Star size={48} className="mx-auto text-gray-300" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">評価データがありません</h3>
        <p className="text-gray-600">この面接の評価はまだ完了していません。</p>
      </div>
      )}

      {/* カスタムスキル */}
      {customSkills && <CustomSkillsSection customSkills={customSkills} />}

      {/* 固定質問への回答 */}
      {fixedQuestionAnswers && fixedQuestionsArray.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h5 className="font-medium text-gray-900 mb-3">固定質問</h5>
          <div className="space-y-4">
            {fixedQuestionsArray.map((qa, index) => (
              <div key={index} className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-green-50 px-4 py-3 border-b border-gray-200">
                  <span className="text-xs font-semibold text-green-700 mr-2">Q{index + 1}</span>
                  <span className="font-medium text-gray-800">{qa.question}</span>
                </div>
                <div className="bg-white px-4 py-3">
                  <span className="text-xs font-semibold text-blue-600 mr-2">A</span>
                  <span className="text-gray-700 leading-relaxed whitespace-pre-line">{qa.answer}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
